import React, { useEffect, useState, useCallback } from "react";

import { decodeToken } from "react-jwt";

import { useVerifyHook } from "../api-client/common/common";
import { useLocalStorage } from "../hooks/useLocalStorage";

/**
 * 認証情報でlocalStorageに投入したデータの型
 */
type AuthInfo = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  email: string;
  role: string;
};

type DecodedJwt = {
  "cognito:groups": string[];
  email: string;
  exp: number;
};
// ログイン状態のContext
/**
 * true: ログイン状態
 * false: ログアウト状態
 * null 未認証状態
 */
export const LoggedInContext = React.createContext<[boolean | null, () => void]>([null, () => { }]);

// 認証情報と認証情報セットのContext
export const AuthInfoContext = React.createContext<
  [
    AuthInfo,
    (
      idToken: string,
      accessToken: string,
      refreshToken: string
    ) => void,
    (email: string) => void,
    (role: string) => void
  ]
>([{ idToken: "", accessToken: "", refreshToken: "", email: "", role: "" }, () => { }, () => { }, () => { }]);

export const AuthContextProvider: React.FC<React.PropsWithChildren> = (
  props
) => {
  // stateの定義
  const [loggedIn, setLoggedIn] = useState<boolean | null>(null);
  const [authInfo, setAuthInfo] = useState<AuthInfo>({
    idToken: "",
    accessToken: "",
    refreshToken: "",
    email: "",
    role: ""
  });
  const [idToken, updateIdToken, deleteIdToken] = useLocalStorage("idToken");
  const [accessToken, updateAccessToken, deleteAccessToken] = useLocalStorage("accessToken");
  const [refreshToken, updateRefreshToken, deleteRefreshToken] = useLocalStorage("refreshToken");

  const checkVerify = useCallback(async () => {
    if (!idToken) {
      setLoggedIn(false);
      return
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const verify = useVerifyHook();
    try {
      await verify();
      setLoggedIn(true);
    } catch (error) {
      setLoggedIn(false);
      return;
    }
  }, [idToken]);

  const setToken = useCallback((
    idToken: string,
    accessToken: string,
    refreshToken: string
  ) => {
    const decodedIdToken = decodeToken<DecodedJwt>(idToken);

    if (decodedIdToken && decodedIdToken?.["cognito:groups"]?.length > 0) {
      setAuthInfo(() => {
        return {
          idToken,
          accessToken,
          refreshToken,
          email: decodedIdToken.email,
          role: decodedIdToken["cognito:groups"][0],
        };
      });
      // Patientに合わせ、まずトークンを永続化する（サイレントリフレッシュはSession側で実施）
      updateIdToken(idToken);
      updateAccessToken(accessToken);
      updateRefreshToken(refreshToken);
    }
  },
    // eslint-disable-next-line
    [updateIdToken, updateAccessToken, updateRefreshToken]
  );

  const setEmail = useCallback((email: string) => {
    setAuthInfo((prevState) => {
      return {
        idToken: prevState.idToken,
        accessToken: prevState.accessToken,
        refreshToken: prevState.refreshToken,
        email,
        role: prevState.role,
      };
    });
  }, []);

  const setRole = useCallback((email: string) => {
    setAuthInfo((prevState) => {
      return {
        idToken: prevState.idToken,
        accessToken: prevState.accessToken,
        refreshToken: prevState.refreshToken,
        email,
        role: prevState.role,
      };
    });
  }, []);
  // extentionRefreshToken はSession側で統一運用するため、Providerからは除去（Patientと同様の責務分担）
  /**
   * 認証チェック：レンダリングされたタイミングで実行
   */
  useEffect(() => {
    checkVerify();
  }, [authInfo, checkVerify, idToken]);

  /**
   * ローカルストレージから復元したタイミングで実行
   */
  useEffect(() => {
    console.debug("setToken() [FROM]localstorage");
    if (idToken && accessToken && refreshToken) {
      setToken(idToken, accessToken, refreshToken);
    }
  }, [setToken, idToken, accessToken, refreshToken]);

  const logout = useCallback(() => {
    setLoggedIn(false);
    setAuthInfo({
      idToken: "",
      accessToken: "",
      refreshToken: "",
      email: "",
      role: "",
    });
    deleteIdToken();
    deleteAccessToken();
    deleteRefreshToken();
  }, [deleteIdToken, deleteAccessToken, deleteRefreshToken]);
  return (
    <LoggedInContext.Provider value={[loggedIn, logout]}>
      <AuthInfoContext.Provider value={[authInfo, setToken, setEmail, setRole]}>
        {props.children}
      </AuthInfoContext.Provider>
    </LoggedInContext.Provider>
  );
};
